import { Injectable, inject } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';

/**
 * Service that provides human-readable, internationalized error messages for validation errors.
 * This service uses ngx-translate to provide localized error messages and can be used globally
 * throughout the application to standardize error message formatting.
 *
 * Translation keys are defined in the shared library under SHARED.VALIDATION.*
 */
@Injectable({
  providedIn: 'root',
})
export class ValidationErrorMessageService {
  private readonly translate = inject(TranslateService);
  /**
   * Generates a human-readable error message from Angular validation errors.
   * Returns the first error found in the validation errors object.
   *
   * @param errors - The validation errors from FormControl
   * @param fieldLabel - Optional field label to include in the error message
   * @returns A human-readable error message or null if no errors
   */
  getErrorMessage(errors: ValidationErrors | null, fieldLabel?: string): string | null {
    if (!errors) {
      return null;
    }

    const fieldName = fieldLabel || this.translate.instant('SHARED.VALIDATION.DEFAULT_FIELD');

    // Handle required validator
    if (errors['required']) {
      return this.translate.instant('SHARED.VALIDATION.REQUIRED', { field: fieldName });
    }

    // Handle pattern validator
    if (errors['pattern']) {
      return this.getPatternErrorMessage(errors['pattern'].requiredPattern, fieldName);
    }

    // Handle email validator
    if (errors['email']) {
      return this.translate.instant('SHARED.VALIDATION.EMAIL', { field: fieldName });
    }

    // Handle min length validator
    if (errors['minlength']) {
      const requiredLength = errors['minlength'].requiredLength;
      return this.translate.instant('SHARED.VALIDATION.MIN_LENGTH', {
        field: fieldName,
        length: requiredLength,
      });
    }

    // Handle max length validator
    if (errors['maxlength']) {
      const requiredLength = errors['maxlength'].requiredLength;
      return this.translate.instant('SHARED.VALIDATION.MAX_LENGTH', {
        field: fieldName,
        length: requiredLength,
      });
    }

    // Handle min validator
    if (errors['min']) {
      const min = errors['min'].min;
      return this.translate.instant('SHARED.VALIDATION.MIN_VALUE', {
        field: fieldName,
        min: min,
      });
    }

    // Handle max validator
    if (errors['max']) {
      const max = errors['max'].max;
      return this.translate.instant('SHARED.VALIDATION.MAX_VALUE', {
        field: fieldName,
        max: max,
      });
    }

    // Handle custom validators or unknown errors
    return this.getCustomErrorMessage(errors, fieldName);
  }

  private getPatternErrorMessage(pattern: string, fieldName: string): string {
    // Common pattern recognition for better user messages
    if (pattern === 'https?://.*' || pattern === '^https?://.*$') {
      return this.translate.instant('SHARED.VALIDATION.PATTERN.URL', { field: fieldName });
    }

    if (pattern.includes('@')) {
      return this.translate.instant('SHARED.VALIDATION.PATTERN.EMAIL', { field: fieldName });
    }

    if (pattern.includes('[0-9]')) {
      return this.translate.instant('SHARED.VALIDATION.PATTERN.NUMERIC', { field: fieldName });
    }

    if (pattern === '^.*\\S.*$') {
      return this.translate.instant('SHARED.VALIDATION.PATTERN.NO_WHITESPACE', {
        field: fieldName,
      });
    }

    return this.translate.instant('SHARED.VALIDATION.PATTERN.INVALID', { field: fieldName });
  }

  private getCustomErrorMessage(errors: ValidationErrors, fieldName: string): string {
    const firstErrorKey = Object.keys(errors)[0];
    if (!firstErrorKey) {
      return this.translate.instant('SHARED.VALIDATION.GENERIC_INVALID', { field: fieldName });
    }

    const errorValue = errors[firstErrorKey];

    if (typeof errorValue === 'string') {
      return errorValue;
    }

    if (errorValue && typeof errorValue === 'object' && errorValue.message) {
      return errorValue.message;
    }

    return this.translate.instant('SHARED.VALIDATION.GENERIC_INVALID', { field: fieldName });
  }

  /**
   * Gets all error messages from validation errors.
   *
   * @param errors - The validation errors from FormControl
   * @param fieldLabel - Optional field label to include in error messages
   * @returns Array of human-readable error messages
   */
  getAllErrorMessages(errors: ValidationErrors | null, fieldLabel?: string): string[] {
    if (!errors) {
      return [];
    }

    const fieldName = fieldLabel || this.translate.instant('SHARED.VALIDATION.DEFAULT_FIELD');
    const messages: string[] = [];

    Object.keys(errors).forEach((key) => {
      const singleError = { [key]: errors[key] };
      const message = this.getErrorMessage(singleError, fieldName);
      if (message) {
        messages.push(message);
      }
    });

    return messages;
  }
}
